import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { TypeOfSourceRequest } from '@base/enums/messages.enum';
import { SendSourcingRequestParams } from '@base/models/requests/messages.res';
import { ProductDetail } from '@base/models/responses/product-detail.res';
import { MeetService } from '@base/services/private/meet.service';
import { ModalService } from '@base/services/private/modal.service';
import { ModalSentSuccessComponent } from '@base/components/private/modals/modal-sent-success/modal-sent-success.component';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { ModalSentFailedComponent } from '@base/components/private/modals/modal-sent-failed/modal-sent-failed.component';
import { Subscription } from 'rxjs';
import { GlobalService } from '@base/services/private/global.service';
import { StatusResponse } from '@base/enums/status-response.enum';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';

@Component({
  selector: 'app-modal-source-request',
  imports: [
    ModalBodyComponent,
    ModalHeaderComponent,
    ReactiveFormsModule,
    forwardRef(() => TranslatePipe),
    ModalFooterComponent,
  ],
  providers: [forwardRef(() => TranslatePipe)],
  templateUrl: './modal-source-request.component.html',
  styleUrl: './modal-source-request.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalSourceRequestComponent implements OnInit, OnDestroy {
  private readonly modalSrv = inject(ModalService);
  private readonly meetSrv = inject(MeetService);
  private readonly fb = inject(FormBuilder);
  private readonly translatePipe = inject(TranslatePipe);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly globalSrv = inject(GlobalService);

  @Input() modalId = '';
  @Input() productDetail?: ProductDetail | null = null;
  @Input() productId = '';

  form = this.fb.group({
    purpose: ['', [Validators.required]],
    unitValue: ['', [Validators.required, Validators.min(1)]],
    quantityType: ['UNITS', [Validators.required]],
    additionalInformation: ['', []],
  });
  purpose = [
    {
      label: 'DIRECT_PURCHASE',
      value: 'DIRECT_PURCHASE',
    },
    {
      label: 'DISTRIBUTE',
      value: 'DISTRIBUTE',
    },
  ];
  quantityType = [
    {
      label: 'UNITS',
      value: 'UNITS',
    },
    {
      label: 'METRES',
      value: 'METRES',
    },
    {
      label: 'FEET',
      value: 'FEET',
    },
    {
      label: 'SQUARE_METRES',
      value: 'SQUARE_METRES',
    },
    {
      label: 'SQUARE_FEET',
      value: 'SQUARE_FEET',
    },
    {
      label: 'KILOGRAMS',
      value: 'KILOGRAMS',
    },
    {
      label: 'POUNDS',
      value: 'POUNDS',
    },
    {
      label: 'METRIC_TONS',
      value: 'METRIC_TONS',
    },
  ];
  chatroomId = '';

  isHttpRequestRunning = false;
  httpRequestObserver$: Subscription | null = null;

  ngOnInit(): void {
    this.listenHttpRequestState();
  }

  ngOnDestroy(): void {
    this.httpRequestObserver$?.unsubscribe();
    this.isHttpRequestRunning = false;
    this.globalSrv.destroyModalRef(this.modalId);
  }

  /**
   * Closes the current modal.
   * @returns void
   */
  close(): void {
    this.modalSrv.close(this.modalId);
  }

  /**
   * Listens for HTTP request state changes and updates the component state accordingly.
   * @returns void
   */
  listenHttpRequestState(): void {
    this.httpRequestObserver$ = this.globalSrv.httpRequestObserver().subscribe({
      next: (res) => {
        this.isHttpRequestRunning = res;
        this.triggerChangeDetection();
      },
    });
  }

  get additionalInformationControl(): AbstractControl {
    return this.form.get('additionalInformation') as AbstractControl;
  }

  get purposeControl(): AbstractControl {
    return this.form.get('purpose') as AbstractControl;
  }

  get unitValueControl(): AbstractControl {
    return this.form.get('unitValue') as AbstractControl;
  }

  get quantityTypeControl(): AbstractControl {
    return this.form.get('quantityType') as AbstractControl;
  }

  /**
   * Shows a modal warning if the account is a preview account and status response is -2.
   */
  showModalWarning(): void {
    this.modalSrv.showModalWarningForPreviewAccount();
    return;
  }

  /**
   * Sends a sourcing request after validating the form.
   * @returns void
   */
  send(): void {
    this.form.markAllAsDirty();
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const payload: SendSourcingRequestParams = {
      company_id: this.productDetail?.company[0].company_id || '',
      text:
        '{{products}}\n\n' +
        this.translatePipe.transform('SOURCING_PURPOSE') +
        ':&nbsp;' +
        this.translatePipe.transform(this.purposeControl.value) +
        '\n' +
        this.translatePipe.transform('QUANTITY') +
        ':&nbsp;' +
        this.unitValueControl.value +
        ' ' +
        this.translatePipe.transform(this.quantityTypeControl.value) +
        '\n' +
        this.translatePipe.transform('ADDITIONAL_SOURCING_REQUIREMENTS') +
        ':&nbsp;' +
        this.additionalInformationControl.value,
      type: TypeOfSourceRequest.RFQ_INQUIRY_VIA_PRODUCT,
      products_id: [this.productId!],
    };
    this.meetSrv.sendSourcingRequest(payload).subscribe({
      next: (res) => {
        if (res.status === StatusResponse.SUCCESS) {
          this.close();
          this.chatroomId = res.chatroom_id;
          this.showMessageSuccessRequest();
        } else if (+res.status === StatusResponse.PREVIEW_ACCOUNT) {
          // Preview account returns status -2, but success returns 'SUCCESS'; always convert to number to check for -2.
          this.showModalWarning();
        } else if (+res.status === StatusResponse.OWN_COMPANY) {
          this.showFailedOwnCompanyRequest();
        } else {
          this.showFailedRequest();
        }
      },
      error: (err) => {
        console.error('Error sending sourcing request:', err);
        this.showFailedRequest();
      },
    });
  }

  /**
   * Displays a modal indicating that the product enquiry request has failed.
   * @returns void
   */
  showFailedRequest(): void {
    this.modalSrv.showModalFailedRequest();
  }

  /**
   * Displays a modal indicating that the product enquiry cannot be sent to the initiator's own company.
   * @returns void
   */
  showFailedOwnCompanyRequest(): void {
    const data: Partial<ModalSentFailedComponent> = {
      header: 'HOLD_ON',
      description: 'UNABLE_TO_SEND_TO_YOUR_OWN_COMPANY',
    };
    this.modalSrv.showModalFailedRequest({
      addsData: data,
    });
  }

  /**
   * Closes the current modal and shows the success request modal.
   * @returns void
   */
  showMessageSuccessRequest(): void {
    this.close(); // first modal
    const data: Partial<ModalSentSuccessComponent> = {
      chatroomId: this.chatroomId || this.productDetail?.prodchat_id,
    };

    this.modalSrv.showModalSuccessRequest({
      addsData: data,
    });
  }

  /**
   * Triggers change detection for the component.
   * @returns void
   */
  triggerChangeDetection() {
    this.changeDetectorRef.detectChanges();
    this.changeDetectorRef.markForCheck();
  }
}
